import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MoveIcon } from 'tdesign-icons-react';
import {
  DragSortContext,
  InputNumber,
  PrimaryTableCol,
  Table,
  TableRowData,
} from 'tdesign-react';

import { IS_SMALL_WIDTH } from '@/utils/navigator';

import { IPageSimpleTableProps, ITableColumns } from '../interface';
import { getRenderFun } from '../render';
import { sortByEditIndex } from '../util/editIndex';
import styles from './index.module.less';

/**
 * @param props
 * @returns
 */
const SimpleSortTable: React.FC<
React.PropsWithChildren<IPageSimpleTableProps>
> = ({
  className,
  tableConfig = {},
  dataList = [],
  dragWidth = 40,
  extendsConfig,
  getData,
  ...props
}) => {
  const [dataSource, setDataSource] = useState<TableRowData[]>([]);
  const listRef = useRef(dataList);

  useImperativeHandle(props.cRef, () => ({
    updateRecord: (record: TableRowData) => {
      updateRecord(record);
    },
  }));

  useEffect(() => {
    listRef.current = dataSource;
  }, [dataSource]);

  useEffect(() => {
    setDataSource(setEditSource(dataList));
  }, [dataList]);

  /** 设置列表参数 */
  const formatColumnsConfig = (cols: ITableColumns[]) => cols
    .filter((item) => {
      if (IS_SMALL_WIDTH && item.className === 'detail-col-th') {
        return false;
      }
      return true;
    })
    .map((item) => {
      const cellKey = item.customRender || item.colKey || '';
      const cell = getRenderFun(cellKey, item.enum);
      return {
        width: '140px',
        cell,
        ...item,
      };
    }) as PrimaryTableCol[];


  const [columnsConfig,
    setColumnsConfig] = useState<PrimaryTableCol[]>(() => formatColumnsConfig(props.columnsConfig));
  const [columns, setColumns] = useState<PrimaryTableCol[]>(columnsConfig);

  useEffect(() => {
    const newColumnsConfig = formatColumnsConfig(props.columnsConfig);
    setColumnsConfig(newColumnsConfig);
  }, [props.columnsConfig]);


  const changeData = (data: TableRowData[]) => {
    setDataSource(setEditSource(data));
    getData(setEditSource(data));
  };

  /**
                   * 更新表格单行数据
                   * @param record
                   */
  const updateRecord = (record: TableRowData) => {
    const index = dataSource.findIndex(item => item.keyIndex === record.keyIndex);
    dataSource[index] = record;
    changeData([...dataSource]);
  };

  /** 设置编辑行 */
  const setEditSource = (list: TableRowData[]) => list?.map((e, i) => ({ ...e, editIndex: i + 1 }));

  /** 拖拽排序 */
  const handleSortInputChange = useCallback(
    (rowIndex: number, value: number) => {
      const orderVideoList = [...listRef.current];
      const list = orderVideoList.slice();
      const index = rowIndex;
      list[index].editIndex = value === undefined ? rowIndex + 1 : parseInt(`${value}`, 10);
      changeData(list);
    },
    [dataSource],
  );

  /** 输入数字排序 */
  const handleSortInputBlur = () => {
    const resultList = [...listRef.current].sort(sortByEditIndex);
    changeData(setEditSource(resultList));
  };

  /** 实时设置排序字段 */
  const onDragSort = (context: DragSortContext<TableRowData>) => {
    changeData(setEditSource(context.newData || []));
  };

  /** 设置拖拽排序列 */
  const dragConfig: PrimaryTableCol[] = useMemo(() => [
    {
      colKey: 'drag',
      className: 'drag-col-th',
      title: '排序',
      width: dragWidth,
      cell: (rowData) => {
        const { row, rowIndex } = rowData;
        const index = dataSource.findIndex(item => item.id === row.id) + 1;
        return (
            <div className={styles.dragCol}>
              <div className={styles.dragIcon}>
                <MoveIcon />
              </div>
              <InputNumber
                onChange={value => handleSortInputChange(rowIndex, value as number)
                }
                theme='normal'
                value={index}
                align='center'
                max={dataSource?.length}
                min={1}
                className={styles.input}
                onBlur={() => handleSortInputBlur()}
              />
            </div>
        );
      },
    },
  ], [dataSource]);

  useEffect(() => {
    if (columnsConfig.length > 0) {
      const configs = dragConfig.concat(columnsConfig);
      setColumns(configs);
    }
  }, [columnsConfig, dragConfig]);

  return (
      <>
        {props.children ? (
          <div className='pageToolsWrap'>{props.children}</div>
        ) : null}
        <div className={styles.tableWrapper}>
          <Table
            rowKey='editIndex'
            data={dataSource || []}
            columns={columns}
            size='small'
            {...tableConfig}
            className={styles.tableList}
            pagination={undefined}
            dragSort='row-handler'
            onDragSort={onDragSort}
          />
        </div>
      </>
  );
};

export default SimpleSortTable;
